import { Box, Flex, Tooltip, ActionIcon, Skeleton, Group } from "@mantine/core";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { OverflownText } from "../../../OverflownText";
import {
  GeneratedChosenIcon,
  DuplicateIcon,
  ProductAreaHeaderIcon,
  SparklesIcon,
  BagelAiIcon,
} from "../../../../icons/x-symbol-svgrepo-com";
import { IconExternalLink, IconCornerDownRight } from "@tabler/icons";
import { Link } from "react-router-dom";
import DuplicateIdeaModal from "../../../ideaNiv/idea-module/DuplicateIdeaModal";
import { useState, useMemo, useContext } from "react";
import { getDuplicateIdea } from "../../../../Api";
import { useAuth0 } from "@auth0/auth0-react";
import { highlightIdeaSearchText } from "../../../../utils/searchHighlight";
import { buildIdeaUrlWithEvidenceFilters } from "../../../../utils/ideasUrlState";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import FiltersContext from "../../filters/context/FiltersContext";
import { formatIdeasRollupCellValue } from "../../ideas-table-helpers/Helpers";

function formatTableCellTextForDisplay(val: unknown): string | number {
  if (val == null) return "";
  if (typeof val === "string" || typeof val === "number") return val;
  if (typeof val === "boolean") return String(val);
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string" || typeof item === "number") return String(item);
        if (typeof item === "boolean") return String(item);
        if (typeof item === "object") {
          const o = item as Record<string, unknown>;
          const pick =
            (typeof o.name === "string" && o.name) ||
            (typeof o.label === "string" && o.label) ||
            (typeof o.title === "string" && o.title) ||
            (typeof o.displayName === "string" && o.displayName);
          if (pick) return pick;
          if (o.amount != null && typeof o.amount !== "object") return String(o.amount);
          if (o.value != null && typeof o.value !== "object") return String(o.value);
        }
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof val === "object") {
    const o = val as Record<string, unknown>;
    const single =
      (typeof o.name === "string" && o.name) ||
      (typeof o.label === "string" && o.label) ||
      (typeof o.title === "string" && o.title) ||
      (typeof o.displayName === "string" && o.displayName);
    if (single) return single;
    if (typeof o.amount === "number" || typeof o.amount === "string") return String(o.amount);
    if (typeof o.value === "number" || typeof o.value === "string") return String(o.value);
    try {
      const s = JSON.stringify(val);
      return s.length > 120 ? `${s.slice(0, 117)}…` : s;
    } catch {
      return "";
    }
  }
  return String(val);
}

export const TextCell: React.FC<any> = ({
  data,
  col,
  renderParams,
  sortState = "total",
}) => {

  const { activeFilters } = useContext(FiltersContext);

  const resolved = getNestedFieldValue(data, col.accessor);
  const rawValue =
    resolved !== undefined && resolved !== null ? resolved : renderParams?.default;
  const auth0 = useAuth0();
  const { canEditIdeas } = useUserPermissions();
  const isReadOnly = !canEditIdeas;
  // Apply highlighting for title column when search is active (uses idea search logic)
  const value = useMemo(() => {
    if (col.accessor === "title" && data.searchQuery && rawValue) {
      return highlightIdeaSearchText(rawValue, data.searchQuery);
    }
    const rollup = formatIdeasRollupCellValue(rawValue, sortState, renderParams);
    if (rollup !== null) return rollup;
    return formatTableCellTextForDisplay(rawValue);
  }, [col.accessor, data.searchQuery, rawValue, sortState, renderParams]);

  const [duplicateIdeaModalOpened, setDuplicateIdeaModalOpened] = useState(false);
  const [duplicateIdeaData, setDuplicateIdeaData] = useState<any>(null);

  const addGeneratedIcon = col.accessor === "title" && data.state === "generated";
  const hasParentIdea = col.accessor === "title" && !!data?.parentIdeaId;

  const stopRowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const stopRowClickPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleDuplicateIdeaClick = async (e: React.MouseEvent) => {
    stopRowClick(e);
    setDuplicateIdeaModalOpened(true);    
    try {
      const duplicateData = await getDuplicateIdea(data?._id, auth0);
      setDuplicateIdeaData(duplicateData);
    } catch (error: any) {
      // 404 is expected when no duplicate exists - just set to null
      if (error?.response?.status === 404) {
        setDuplicateIdeaData(null);
      } else {
        console.error('Error fetching duplicate idea data:', error);
      }
    } 
  };

  const color = (data?. componentIdSource === 'ml' && data?.componentObj?._id) ? '#5C5CEB' : "#212529";

  
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        position: "relative",
      }}
      className={`text-cell-${col.accessor.replace(/\./g, '-')}`}
      onClick={(e) => {
        if (duplicateIdeaModalOpened) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Group spacing={4} align="center" noWrap>
        {col.accessor === "componentObj.name" && (
          <Box mr={8}>
            <Flex style={{ display: 'flex', alignItems: 'center'}}>
              <ProductAreaHeaderIcon  color={color} fill={color} />
              {(data?.componentObj?._id && data?. componentIdSource === 'ml') && (
                <Box mb={-3} ml={3}>
                  <SparklesIcon color={'#5B60E3'} />
                </Box>
              )}
            </Flex>
          </Box>
        )}
        {hasParentIdea && (
          <Tooltip label="Sub Idea" position="top" withinPortal>
            <Box
              component={Link}
              to={buildIdeaUrlWithEvidenceFilters(data.parentIdeaId, window.location.search, activeFilters)}
              onClick={stopRowClickPropagation}
              w={22}
              pr={3}
              mr={6}
              m={0}
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                color: "inherit",
                textDecoration: "none",
              }}
            >
              <IconCornerDownRight size={16} color="#585C68" />
            </Box>
          </Tooltip>
        )}
        {data?.duplicateIdeas && col.accessor === "title" && !isReadOnly && (
          <Tooltip
            position="top"
            label={"Duplication Alert"}
            withinPortal
          >
            <Box
              w={22}
              pr={3}
              mr={6}
              m={0}
              sx={{ display: "flex", alignItems: "center" }}
              onClick={handleDuplicateIdeaClick}
            >
              <DuplicateIcon />
            </Box>
          </Tooltip>
        )}
        {addGeneratedIcon && (
          <Tooltip
            position="top"
            label={"Generated by Bagel AI"}
            withinPortal
          >
            <Box
              w={22}
              pr={3}
              mr={6}
              m={0}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <BagelAiIcon />
            </Box>
          </Tooltip>
        )}
        <OverflownText
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxWidth: '99%',
            color: (col.accessor === "componentObj.name" && data?.componentIdSource === 'ml' && data?.componentObj?._id) ? '#5B60E3' : ''
          }}
          overTooltip={renderParams.tooltip}
        >
          {value ?? ""}
        </OverflownText>

        {/* duplicate idea modal */}
        {duplicateIdeaModalOpened && 
        <DuplicateIdeaModal
          idea={data}
          duplicateIdea={duplicateIdeaData}
          duplicationType={data?.duplicationType}
          duplicationId={data?.duplicationId}
          verifyOpened={duplicateIdeaModalOpened}
          setVerifyOpened={setDuplicateIdeaModalOpened}
        />}
      </Group>
      <Tooltip label="Open idea in new tab" withArrow withinPortal>
        <ActionIcon
          className={`external-link-icon external-link-${col.accessor.replace(/\./g, '-')}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const isThemeRow = !!data?._isThemeRow;
            const targetIdeaId = isThemeRow && data?.parentIdeaId ? data.parentIdeaId : data._id;
            const selectedThemeTitle = isThemeRow ? (data?.title || "") : "";
            window.open(
              buildIdeaUrlWithEvidenceFilters(
                targetIdeaId,
                window.location.search,
                activeFilters,
                selectedThemeTitle ? { selectedTheme: selectedThemeTitle } : undefined,
              ),
              "_blank",
            );
          }}
          variant="transparent"
          size="sm"
          sx={{ 
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: "0px",
            zIndex: 10,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(2px)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
            "&:active": {
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
            "&:focus": {
              transform: "translateY(-50%)",
            }
          }}
        >
          <IconExternalLink size={18} color="#5b60e3" />
        </ActionIcon>
      </Tooltip>
    </Box>
  );
};

// Skeleton component for TextCell
export const TextCellSkeleton: React.FC<{ col: any; id?: string }> = ({ col, id }) => {
  const isTitle = col.accessor === "title";
  const renderParams = col.renderParams || {};
  
  // Calculate width based on column configuration
  const maxWidth = renderParams.maxWidth || col.maxWidth || "350px";
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  
  // Determine skeleton width with memoized random value to prevent changes on re-render
  const skeletonWidth = useMemo(() => {
    if (isTitle) {
      return "350px"; // Fixed width for title
    } else {
      // Generate a deterministic random width based on id (with index) or column accessor
      const seedString = id || col.accessor;
      const hash = seedString.split('').reduce((acc: number, char: string) => {
        return acc + char.charCodeAt(0);
      }, 0);
      const percentage = 40 + (hash % 51); // Results in 40-90%
      return `${percentage}%`;
    }
  }, [isTitle, col.accessor, id]);
  
  return (
    <Box
      sx={{
        maxWidth,
        width: columnWidth || "100%",
        minWidth,
        position: "relative",
        paddingRight: "23px",
      }}
    >
      <Flex align="center">
        <Skeleton height={16} width={skeletonWidth} radius="sm" />
      </Flex>
    </Box>
  );
};
